import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Component, computed, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { MatDialog } from '@angular/material/dialog';
import { TranslocoService } from '@jsverse/transloco';
import { AnalysisApiService } from 'src/app/data-transfer/services/analysis-api-service';
import { PackagingPart } from 'src/app/model/packaging-part-enum';
import { AnalysisNotPossibleDialogComponent } from 'src/app/components/dialogs/analysis-not-possible-dialog/analysis-not-possible-dialog.component';

// ============================================================================
// Types
// ============================================================================

export type Rating = 'UNKNOWN' | 'GREEN' | 'YELLOW' | 'RED';

export interface GmsRating {
  rating: Rating;
  percentage: number;
}
export interface GmsLayerResult {
  id: string;
  analyzedObject: string;
  ratings: GmsRating[];
}
export interface GmsComponentResult {
  id: string;
  analyzedObject: string;
  results: GmsLayerResult[];
}
export interface GmsSortingFractionResult {
  id: string;
  sortingFraction: string;
  results: GmsComponentResult[];
}
export type GmsLogLevel = 'Trace' | 'Debug' | 'Information' | 'Warning' | 'Error';
export interface GmsLogMessage {
  content: string;
  logLevel: GmsLogLevel;
}
export interface GmsResult {
  id: string;
  location: string;
  evaluationLog: { messages: GmsLogMessage[] };
  evaluationTimestamp: string;
  results: GmsSortingFractionResult[];
}
export interface GmsEvaluationResponse {
  id: string;
  algorithmVersion: string;
  analyzedObject: string;
  referenceTimestamp: string;
  results: GmsResult[];
}

export interface MaterialLayer {
  guid: string;
  functionName: string;
  materialName: string;
  materialManifestationName: string;
  mass: number;
  colorName: string | null;
  index: number;
}
export interface MultiMaterial {
  guid: string;
  totalWeight: number;
  layers: MaterialLayer[];
}
export interface PackagingComponent {
  id: number;
  packagingComponentTypeName: string;
  packagingComponentSubtypeName: string;
  packagingComponentCategoryName: string;
  articleName: string;
  isRigidComponent: boolean | null;
  multiMaterial: MultiMaterial;
}
export interface ComponentEntry {
  guid: string;
  underlyingComponent: PackagingComponent;
}
export interface PackagingUnit {
  id: number;
  guid: string;
  version: number;
  brandName: string;
  productName: string;
  packagingTypeName: string;
  assemblyCountry: string;
  distributionCountries: string[];
  mainBody: ComponentEntry;
  closures: ComponentEntry[];
  labels: ComponentEntry[];
  sleeves: ComponentEntry[];
  inlays: ComponentEntry[];
  inMoldLabels: ComponentEntry[];
  packagingAids: ComponentEntry[];
}
export interface EvaluateRecyclabilityRequest {
  id: number;
  version: number;
  ignoreCustomerAssumptions: boolean;
  evaluationReferenceTimestamp: string;
  evaluationCountries: string[];
}

export interface TranslationDto {
  culture: string;
  translatedName: string;
}
export interface WasteStreamTranslationDto extends TranslationDto {
  location: string;
  translatedDescription: string;
}
export interface WasteStreamDto {
  id: string;
  historicCustomerBackendId: number | null;
  historicEvaluationBackendId: number | null;
  fallbackName: string;
  translations: WasteStreamTranslationDto[];
}

// ----- view models ----------------------------------------------------------

export interface LayerViewModel {
  guid: string;
  functionName: string;
  materialName: string;
  manifestationName: string;
  mass: number;
  color: string | null;
  rating: Rating;
  ratingPercentage: number;
}
export interface ComponentViewModel {
  guid: string;
  groupName: string;
  typeName: string;
  subtypeName: string;
  articleName: string;
  totalWeight: number;
  isRigid: boolean | null;
  layers: LayerViewModel[];
  aggregateRating: Rating;
}
export interface SortingFractionViewModel {
  id: string;
  fractionGuid: string;
  name: string;
  componentGuids: string[];
}

// ============================================================================
// Component
// ============================================================================

@Component({
  selector: 'app-recyclability-result',
  templateUrl: './recyclability-result.component.html',
  styleUrls: ['./recyclability-result.component.scss'],
})
export class RecyclabilityResultComponent {
  // Form fields
  id: number | null = 4675;
  version: number | null = 6;

  // Fixed request body values
  evaluationCountries: string[] = ['DE'];
  evaluationReferenceTimestamp = '2026-06-01T00:00:00+00:00';
  ignoreCustomerAssumptions = false;

  sortingFractionNames: Record<string, string> = {};

  packagingUnit?: PackagingUnit;

  // evaluation as signal so computed logMessages reacts to changes
  readonly evaluationSignal = signal<GmsEvaluationResponse | undefined>(undefined);

  get evaluation(): GmsEvaluationResponse | undefined {
    return this.evaluationSignal();
  }
  set evaluation(val: GmsEvaluationResponse | undefined) {
    this.evaluationSignal.set(val);
  }

  readonly components = signal<ComponentViewModel[]>([]);
  readonly fractions = signal<SortingFractionViewModel[]>([]);
  readonly routingNote = signal<string | null>(null);
  readonly error = signal<string | null>(null);
  readonly loading = signal<boolean>(false);

  // Log
  logExpanded: Record<string, boolean> = {};
  readonly logLevels: GmsLogLevel[] = ['Error', 'Warning', 'Information', 'Debug'];
  private activeLogLevels = new Set<GmsLogLevel>(['Error', 'Warning', 'Information']);

  readonly logMessages = computed<GmsLogMessage[]>(() => (this.evaluationSignal()?.results ?? []).flatMap(r => r.evaluationLog?.messages ?? []));

  filteredLogMessages(): GmsLogMessage[] {
    return this.logMessages().filter(m => this.activeLogLevels.has(m.logLevel));
  }

  isLogLevelActive(level: GmsLogLevel): boolean {
    return this.activeLogLevels.has(level);
  }

  toggleLogLevel(level: GmsLogLevel): void {
    if (this.activeLogLevels.has(level)) {
      this.activeLogLevels.delete(level);
    } else {
      this.activeLogLevels.add(level);
    }
  }

  isLogExpandedForFraction(fractionId: string): boolean {
    return !!this.logExpanded[fractionId];
  }

  toggleLogForFraction(fractionId: string): void {
    this.logExpanded[fractionId] = !this.logExpanded[fractionId];
  }

  logMessagesForFraction(f: SortingFractionViewModel): GmsLogMessage[] {
    const allMessages = (this.evaluationSignal()?.results ?? []).flatMap(r => r.evaluationLog?.messages ?? []);

    // Split into blocks at each "Starting to evaluate Packaging Component Collection" message
    const blocks: GmsLogMessage[][] = [];
    let current: GmsLogMessage[] = [];
    for (const m of allMessages) {
      if (m.content.includes('Starting to evaluate Packaging Component Collection')) {
        if (current.length > 0) blocks.push(current);
        current = [m];
      } else {
        current.push(m);
      }
    }
    if (current.length > 0) blocks.push(current);

    // Assign block by fraction index
    const fractionIndex = this.fractions().findIndex(fr => fr.id === f.id);
    return blocks[fractionIndex] ?? [];
  }

  filteredLogMessagesForFraction(f: SortingFractionViewModel): GmsLogMessage[] {
    return this.logMessagesForFraction(f).filter(m => this.activeLogLevels.has(m.logLevel));
  }

  private readonly severity: Record<Rating, number> = {
    GREEN: 0,
    UNKNOWN: 1,
    YELLOW: 2,
    RED: 3,
  };

  readonly componentsByGuid = computed(() => {
    const m = new Map<string, ComponentViewModel>();
    for (const c of this.components()) m.set(c.guid, c);
    return m;
  });

  readonly ratingTally = computed(() => {
    const t: Record<Rating, number> = { GREEN: 0, YELLOW: 0, RED: 0, UNKNOWN: 0 };
    for (const c of this.components()) for (const l of c.layers) t[l.rating]++;
    return t;
  });

  constructor(
    private readonly http: HttpClient,
    private readonly sanitizer: DomSanitizer,
    private readonly dialog: MatDialog,
    private readonly translocoService: TranslocoService,
    private readonly analysisApiService: AnalysisApiService
  ) {}

  // --------------------------------------------------------------------------
  // Form action
  // --------------------------------------------------------------------------

  evaluate(): void {
    if (this.id == null || this.version == null) return;
    this.error.set(null);
    this.loading.set(true);
    this.evaluationSignal.set(undefined);
    this.logExpanded = {};
    const id = this.id;
    const version = this.version;

    this.analysisApiService.getRecyclabilityAnalysisProblemInformation(id, PackagingPart.Unit, version).subscribe({
      next: problemInformation => {
        if (problemInformation.length > 0) {
          this.loading.set(false);
          this.dialog.open(AnalysisNotPossibleDialogComponent, {
            data: {
              generalExplanation: this.translocoService.translate('analysisNotPossibleInfo.generalConditionsUnit'),
              problemInformation,
            },
          });
          return;
        }
        this.proceedWithEvaluation(id, version);
      },
      error: () => {
        // if check fails, proceed anyway
        this.proceedWithEvaluation(id, version);
      },
    });
  }

  private proceedWithEvaluation(id: number, version: number): void {
    this.getPackagingUnitById(id, version).subscribe({
      next: unit => {
        this.packagingUnit = unit;
        this.evaluateRecyclability(id, version).subscribe({
          next: evaluation => {
            this.evaluationSignal.set(evaluation);
            this.getWasteStreams().subscribe({
              next: (streams: WasteStreamDto[]) => {
                this.sortingFractionNames = Object.fromEntries(
                  streams.map(s => {
                    const translation = s.translations?.find(t => t.culture === 'de-DE') ?? s.translations?.find(t => t.culture?.startsWith('de')) ?? s.translations?.[0];
                    return [s.id, translation?.translatedName ?? s.fallbackName];
                  })
                );
                this.rebuild();
                this.loading.set(false);
              },
              error: () => {
                this.rebuild();
                this.loading.set(false);
              },
            });
          },
          error: () => {
            this.loading.set(false);
            this.error.set('Could not evaluate recyclability.');
          },
        });
      },
      error: () => {
        this.loading.set(false);
        this.error.set('Could not load the packaging unit.');
      },
    });
  }

  // --------------------------------------------------------------------------
  // API requests
  // --------------------------------------------------------------------------

  public getPackagingUnitById(id: number, version: number): Observable<PackagingUnit> {
    const url = `${environment.webApiAddress}/api/packaging-units/by-id?id=${id}&version=${version}`;
    return this.http.get<PackagingUnit>(url);
  }

  public getWasteStreams(): Observable<WasteStreamDto[]> {
    return this.http.get<WasteStreamDto[]>(`${environment.webApiAddress}/api/waste-streams`, {
      headers: { 'Accept-Version': '4.0' },
    });
  }

  public evaluateRecyclability(id: number, version: number): Observable<GmsEvaluationResponse> {
    const url = `${environment.webApiAddress}/api/packaging-units/evaluate-recyclability/gms`;
    const body: EvaluateRecyclabilityRequest = {
      id,
      version,
      ignoreCustomerAssumptions: this.ignoreCustomerAssumptions,
      evaluationReferenceTimestamp: this.evaluationReferenceTimestamp,
      evaluationCountries: this.evaluationCountries,
    };
    return this.http.post<GmsEvaluationResponse[]>(url, body, { headers: { 'Accept-Version': '4.0' } }).pipe(map(arr => arr[0]));
  }

  // --------------------------------------------------------------------------
  // Mapping
  // --------------------------------------------------------------------------

  private rebuild(): void {
    if (!this.packagingUnit || !this.evaluationSignal()) return;
    this.components.set(this.buildComponents());
    this.fractions.set(this.buildSortingFractions());
    this.routingNote.set(this.finalRoutingMessage());
    this.buildGuidNames();
  }

  private collectEntries(): Array<{ group: string; entry: ComponentEntry }> {
    const u = this.packagingUnit!;
    const groups: Array<[string, ComponentEntry[]]> = [
      ['Main Body', u.mainBody ? [u.mainBody] : []],
      ['Closure', u.closures ?? []],
      ['Label', u.labels ?? []],
      ['Sleeve', u.sleeves ?? []],
      ['Inlay', u.inlays ?? []],
      ['In-Mold Label', u.inMoldLabels ?? []],
      ['Packaging Aid', u.packagingAids ?? []],
    ];
    return groups.flatMap(([group, entries]) => entries.map(entry => ({ group, entry })));
  }

  private indexGmsResults(): Map<string, Map<string, GmsLayerResult>> {
    const byComponent = new Map<string, Map<string, GmsLayerResult>>();
    const compResults = (this.evaluationSignal()!.results ?? []).flatMap(r => r.results ?? []).flatMap(f => f.results ?? []);
    for (const comp of compResults) {
      const layerMap = byComponent.get(comp.analyzedObject) ?? new Map<string, GmsLayerResult>();
      for (const layer of comp.results ?? []) layerMap.set(layer.analyzedObject, layer);
      byComponent.set(comp.analyzedObject, layerMap);
    }
    return byComponent;
  }

  private dominantRating(layer?: GmsLayerResult): { rating: Rating; percentage: number } {
    if (!layer?.ratings?.length) return { rating: 'UNKNOWN', percentage: 100 };
    return layer.ratings.reduce((best, cur) => (cur.percentage > best.percentage ? cur : best));
  }

  private aggregate(layers: LayerViewModel[]): Rating {
    if (!layers.length) return 'UNKNOWN';
    return layers.reduce<Rating>((worst, l) => (this.severity[l.rating] > this.severity[worst] ? l.rating : worst), 'GREEN');
  }

  private buildComponents(): ComponentViewModel[] {
    const gmsIndex = this.indexGmsResults();
    return this.collectEntries().map(({ group, entry }) => {
      const comp = entry.underlyingComponent;
      const layerResults = gmsIndex.get(entry.guid) ?? new Map<string, GmsLayerResult>();
      const layers: LayerViewModel[] = (comp.multiMaterial?.layers ?? []).map(layer => {
        const { rating, percentage } = this.dominantRating(layerResults.get(layer.guid));
        return {
          guid: layer.guid,
          functionName: layer.functionName,
          materialName: layer.materialName,
          manifestationName: layer.materialManifestationName,
          mass: layer.mass,
          color: layer.colorName,
          rating,
          ratingPercentage: percentage,
        };
      });
      return {
        guid: entry.guid,
        groupName: group,
        typeName: comp.packagingComponentTypeName,
        subtypeName: comp.packagingComponentSubtypeName,
        articleName: comp.articleName,
        totalWeight: comp.multiMaterial?.totalWeight ?? 0,
        isRigid: comp.isRigidComponent,
        layers,
        aggregateRating: this.aggregate(layers),
      };
    });
  }

  private buildSortingFractions(): SortingFractionViewModel[] {
    return (this.evaluationSignal()!.results ?? [])
      .flatMap(r => r.results ?? [])
      .map(f => ({
        id: f.id,
        fractionGuid: f.sortingFraction,
        name: this.sortingFractionNames[f.sortingFraction] ?? f.sortingFraction,
        componentGuids: (f.results ?? []).map(c => c.analyzedObject),
      }));
  }

  private finalRoutingMessage(): string | null {
    const messages = this.evaluationSignal()!.results?.[0]?.evaluationLog?.messages ?? [];
    const info = messages.filter(m => m.logLevel === 'Information');
    return info.length ? info[info.length - 1].content : null;
  }

  // --------------------------------------------------------------------------
  // GUID → name lookup
  // --------------------------------------------------------------------------

  guidNames: Map<string, string> = new Map();

  private buildGuidNames(): void {
    const map = new Map<string, string>();
    if (!this.packagingUnit) return;

    for (const { group, entry } of this.collectEntries()) {
      const comp = entry.underlyingComponent;
      const compLabel = `${group} · ${comp.packagingComponentSubtypeName}${comp.articleName ? ' · ' + comp.articleName : ''}`;
      map.set(entry.guid, compLabel);

      for (const layer of comp.multiMaterial?.layers ?? []) {
        const layerLabel = `${compLabel} → ${layer.materialName}${
          layer.materialManifestationName !== layer.materialName ? ' (' + layer.materialManifestationName + ')' : ''
        } [Layer ${layer.index + 1}]`;
        map.set(layer.guid, layerLabel);
      }
    }

    this.guidNames = map;
  }

  resolveGuids(content: string): SafeHtml {
    const resolved = content.replace(/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi, guid => {
      const name = this.guidNames.get(guid.toLowerCase());
      return name ? `<strong title="${guid}">${name}</strong>` : `<span class="rc-log__guid" title="${guid}">${guid}</span>`;
    });
    return this.sanitizer.bypassSecurityTrustHtml(resolved);
  }

  // --------------------------------------------------------------------------
  // Template helpers
  // --------------------------------------------------------------------------

  componentsForFraction(f: SortingFractionViewModel): ComponentViewModel[] {
    const lookup = this.componentsByGuid();
    return f.componentGuids.map(g => lookup.get(g)).filter((c): c is ComponentViewModel => !!c);
  }

  ratingLabel(rating: Rating): string {
    switch (rating) {
      case 'GREEN':
        return 'Recyclable';
      case 'YELLOW':
        return 'Limited';
      case 'RED':
        return 'Not recyclable';
      default:
        return 'Unknown';
    }
  }

  trackByGuid(_: number, item: { guid: string }): string {
    return item.guid;
  }
  trackByFraction(_: number, item: SortingFractionViewModel): string {
    return item.id;
  }
}
